import { action, computed, makeObservable, observable, reaction } from "mobx";
import type { Query } from "nextjs-routes";
import {
  deserialize,
  optional,
  type PropSchema,
  primitive,
  serializable,
} from "serializr";
import { getQuery, saveQuery } from "../../url";

const asNumber: Pick<PropSchema, "afterDeserialize"> = {
  afterDeserialize(cb, _err, raw) {
    if (raw === "") return cb(undefined, undefined);

    const val = Number(raw);

    if (Number.isNaN(val)) {
      console.warn(`SearchParamsDTO: Invalid value`, raw);
      return cb(undefined, undefined);
    }

    cb(undefined, val);
  },
};

const PER_PAGE_OPTIONS = [10, 20, 50] as const;

class SearchParamsDTO {
  @serializable(optional(primitive(asNumber)))
  first?: number;

  @serializable(optional(primitive(asNumber)))
  page?: number;
}

export type PaginationOptions = {
  page?: number;
  perPage?: number;
  perPageOptions?: number[];
  onChangePagination?: PaginationState["onChangePagination"];
};

export class PaginationState {
  readonly key: string;
  @observable count?: number;
  @observable.ref initialPage;
  @observable currentPage;
  @observable firstItem?: number | null;
  @observable lastItem?: number | null;
  @observable lastPage?: number | null;
  @observable perPage: number = PER_PAGE_OPTIONS[0];
  defaultPerPage: number = PER_PAGE_OPTIONS[0];
  @observable total?: number | null;

  /**
   * Triggered by:
   *  @method setPerPage
   *  @method setPage
   *  @method resetPage
   *  @method inc
   *  @method dec
   */
  private onChangePagination?: (arg: PaginationState) => void;

  readonly perPageOptions: readonly number[] = PER_PAGE_OPTIONS;

  constructor(key: string, opts?: PaginationOptions) {
    makeObservable(this);
    const page = opts?.page !== undefined ? opts.page : 1;
    this.key = key;
    if (opts?.perPageOptions) this.perPageOptions = opts.perPageOptions;
    this.onChangePagination = opts?.onChangePagination;
    this.defaultPerPage = opts?.perPage ?? this.perPageOptions[0];
    this.currentPage = page;
    this.initialPage = page;
    this.persistPerPageToLocalStorage();
  }

  @action.bound
  setPerPage(val: number) {
    this.perPage = val;
    this.onChangePagination?.(this);
  }

  @action.bound
  setPage(val: number) {
    this.currentPage = val;
    this.onChangePagination?.(this);
  }

  @action.bound
  resetPage() {
    this.currentPage = this.initialPage;
    this.onChangePagination?.(this);
  }

  @action.bound
  inc() {
    this.currentPage += 1;
    this.onChangePagination?.(this);
  }

  @action.bound
  dec() {
    this.currentPage -= 1;
    this.onChangePagination?.(this);
  }

  @action setFromPaginatorInfo = (
    src: Pick<
      PaginationState,
      | "currentPage"
      | "perPage"
      | "total"
      | "firstItem"
      | "lastItem"
      | "lastPage"
      | "count"
    >,
  ) => {
    if (!src) return;
    this.currentPage = src.currentPage;
    this.perPage = src.perPage;
    this.total = src.total;
    this.firstItem = src.firstItem;
    this.lastItem = src.lastItem;
    this.lastPage = src.lastPage;
    this.count = src.count;
  };

  @action
  fromURLSearchParam(src: Query) {
    const perPage = Number(
      localStorage.getItem(this.persistKey) ?? this.defaultPerPage,
    );

    let querySource = src;
    let loadedFromSession = false;

    // Load from session storage if current query is empty
    const srcObj = src as any;
    if (!srcObj || (!srcObj.page && !srcObj.first)) {
      const savedQuery = getQuery();
      if (savedQuery && (savedQuery.page || savedQuery.first)) {
        querySource = savedQuery;
        loadedFromSession = true;
      }
    }

    const { first, page } = deserialize(SearchParamsDTO, querySource);
    this.setPage(page ?? 1);
    this.setPerPage(first ?? perPage);

    // Update session storage if we loaded from URL (not from session)
    // This ensures shared links update the saved state
    if (!loadedFromSession && (srcObj?.page || srcObj?.first)) {
      saveQuery(src);
    }
  }

  persistPerPageToLocalStorage() {
    try {
      this.perPage = Number(
        localStorage.getItem(this.persistKey) ?? this.defaultPerPage,
      );
      reaction(
        () => this.perPage,
        (perPage) => {
          localStorage.setItem(this.persistKey, String(perPage));
        },
      );
    } catch (_e) {}
  }

  @computed get persistKey() {
    return `perPage-${this.key}`;
  }

  @computed get hasPrevPage() {
    return this.currentPage > 1;
  }

  @computed get hasNextPage() {
    return this.currentPage < (this.lastPage ?? 0);
  }

  @computed get asParams() {
    return {
      page: this.currentPage,
      first: this.perPage,
    };
  }

  @computed get asURLSearchParam() {
    return {
      page: String(this.currentPage),
      first: String(this.perPage),
    };
  }

  @computed get canLoadMore(): boolean {
    return (
      (this.total === undefined && this.currentPage === this.initialPage) ||
      this.hasNextPage ||
      (this.lastItem ?? 0) < (this.total ?? 0)
    );
  }
}
